import React, { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { DocumentReferences } from '../../ChatInDoc/DocumentReferences';
import { parseSupportDocumentReferences, stripFonteLines } from '../../../utils/parseSupportDocumentReferences';
import { normalizeMarkdownTables } from '../../../utils/normalizeMarkdownTables';

interface SupportBotMessageContentProps {
  content?: string | null;
  contentAttributes?: string | null;
}

const markdownComponents = {
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="mb-2 text-base font-bold">{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="mb-2 text-sm font-bold">{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="mb-1 text-sm font-semibold">{children}</h3>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="mb-2 list-disc space-y-1 pl-5 last:mb-0">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="mb-2 list-decimal space-y-1 pl-5 last:mb-0">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="leading-relaxed">{children}</li>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold">{children}</strong>
  ),
  em: ({ children }: { children?: React.ReactNode }) => (
    <em className="italic">{children}</em>
  ),
  a: ({ children, href }: { children?: React.ReactNode; href?: string }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline underline-offset-2"
    >
      {children}
    </a>
  ),
  code: ({ children }: { children?: React.ReactNode }) => (
    <code className="rounded bg-black/10 px-1 py-0.5 text-[13px] font-mono">{children}</code>
  ),
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="my-2 overflow-x-auto">
      <table className="w-full border-collapse text-[13px]">{children}</table>
    </div>
  ),
  thead: ({ children }: { children?: React.ReactNode }) => (
    <thead className="bg-black/5">{children}</thead>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="border border-black/15 px-2 py-1.5 text-left font-semibold">{children}</th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="border border-black/15 px-2 py-1.5 align-top">{children}</td>
  ),
  tr: ({ children }: { children?: React.ReactNode }) => (
    <tr>{children}</tr>
  ),
};

export const SupportBotMessageContent: React.FC<SupportBotMessageContentProps> = ({
  content,
  contentAttributes,
}) => {
  const references = useMemo(
    () => parseSupportDocumentReferences(contentAttributes, content),
    [contentAttributes, content],
  );
  const displayContent = useMemo(
    () =>
    {
      const withoutFonte = references.length > 0 ? stripFonteLines(content) : (content ?? '');
      return normalizeMarkdownTables(withoutFonte);
    },
    [content, references.length],
  );
  const [expanded, setExpanded] = useState(references.length > 0);

  if (!displayContent && references.length === 0)
  {
    return <em className="opacity-70">(sem conteúdo)</em>;
  }

  return (
    <div className="min-w-0">
      {displayContent && (
        <div className="[&>*:last-child]:mb-0">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {displayContent}
          </ReactMarkdown>
        </div>
      )}

      {references.length > 0 && (
        <>
          <div className="mt-1 mb-1">
            <button
              type="button"
              onClick={() => setExpanded((current) => !current)}
              className="text-xs underline underline-offset-2 opacity-80 hover:opacity-100 inline-flex items-center"
            >
              Referências ({references.length})
              {expanded ? (
                <ChevronUpIcon className="h-3 w-3 ml-1" />
              ) : (
                <ChevronDownIcon className="h-3 w-3 ml-1" />
              )}
            </button>
          </div>
          <DocumentReferences
            references={references}
            isVisible={expanded}
          />
        </>
      )}
    </div>
  );
};
